function isVisible(el) {
  // offsetParent is null for display:none, but also for fixed; this is "good enough" here
  return !!(el && (el.offsetWidth || el.offsetHeight || el.getClientRects().length));
}

function controlIsMandatory(control) {
  if (!(control instanceof HTMLElement)) return false;
  // Native required + common powermail-required markers.
  return control.hasAttribute('required')
    || control.getAttribute('aria-required') === 'true'
    || control.hasAttribute('data-powermail-required-message');
}

function getMandatoryFieldwraps(fieldset) {
  return Array.from(fieldset.querySelectorAll('.powermail_fieldwrap')).filter((wrap) => {
    if (wrap.classList.contains('is-hidden')) return false;
    // Mandatory detection based on actual controls.
    return Array.from(wrap.querySelectorAll('input, select, textarea'))
      .some((control) => !control.hasAttribute('disabled') && controlIsMandatory(control));
  });
}

function fieldwrapHasError(fieldwrap) {
  // Primary signal requested: form-control gets powermail_field_error
  if (fieldwrap.querySelector('.form-control.powermail_field_error')) {
    return true;
  }
  // Secondary signal: powermail also applies error class on wrappers (e.g. checkbox items)
  if (fieldwrap.querySelector('.powermail_field_error')) {
    return true;
  }
  // Fallback: powermail may render error containers without applying classes immediately
  const errorContainers = fieldwrap.querySelectorAll('.powermail_field_error_container, .powermail-errors-list');
  for (const el of errorContainers) {
    if (!isVisible(el)) continue;
    if (String(el.textContent || '').trim().length > 0) return true;
  }
  return false;
}

function fieldwrapHasValue(fieldwrap, { includeHiddenControls = false } = {}) {
  const controls = Array.from(
    fieldwrap.querySelectorAll('input, select, textarea'),
  ).filter((c) => {
    if (c.disabled) return false;
    if (!includeHiddenControls && !isVisible(c)) return false;
    return true;
  });

  if (!controls.length) return true;

  // Grouped radios/checkboxes: at least one checked
  const radioLike = controls.filter(
    (c) => c.tagName === 'INPUT' && (c.type === 'checkbox' || c.type === 'radio'),
  );
  if (radioLike.length) {
    return radioLike.some((c) => c.checked);
  }

  // Select: not empty
  const selects = controls.filter((c) => c.tagName === 'SELECT');
  if (selects.length) {
    return selects.every((s) => String(s.value || '').trim().length > 0);
  }

  // Text-ish inputs/textareas: non-empty
  return controls.every((c) => {
    return String(c.value || '').trim().length > 0;
  });
}

function isFieldsetValid(fieldset, { onlyTouched = false, includeHiddenControls = true } = {}) {
  // "onlyTouched" is kept for backward compatibility; current behavior validates immediately.
  void onlyTouched;
  const mandatoryWraps = getMandatoryFieldwraps(fieldset);
  if (!mandatoryWraps.length) return true;

  return mandatoryWraps.every((wrap) => {
    if (fieldwrapHasError(wrap)) return false;
    return fieldwrapHasValue(wrap, { includeHiddenControls });
  });
}

function updateNextButtonState(fieldset) {
  const nextButton = fieldset.querySelector('[data-powermail-step-next]');
  if (!nextButton) return;

  const valid = isFieldsetValid(fieldset);
  nextButton.disabled = !valid;
  nextButton.classList.toggle('is-disabled', !valid);
  nextButton.setAttribute('aria-disabled', String(!valid));
}

function updateSubmitButtonState(form, fieldsets) {
  const submitBtn = form.querySelector('.powermail_fieldwrap_type_submit button[type="submit"]')
    || form.querySelector('button[type="submit"]');
  if (!submitBtn) return;

  const allValid = fieldsets.every((fs) => {
    if (!(fs instanceof HTMLElement)) return true;
    if (fs.classList.contains('is-hidden')) return true;
    return isFieldsetValid(fs);
  });

  submitBtn.disabled = !allValid;
  submitBtn.classList.toggle('is-disabled', !allValid);
  submitBtn.setAttribute('aria-disabled', String(!allValid));
}

export function initPowermailValidation() {
  const forms = document.querySelectorAll('.tx-powermail form.powermail_form');
  if (!forms.length) return;

  forms.forEach((form) => {
    const fieldsets = Array.from(form.querySelectorAll('.powermail_fieldset'));
    if (!fieldsets.length) return;

    const flushFormValidation = () => {
      fieldsets.forEach(updateNextButtonState);
      updateSubmitButtonState(form, fieldsets);
    };

    let validationFramePending = false;
    const scheduleFormValidation = () => {
      if (validationFramePending) return;
      validationFramePending = true;
      requestAnimationFrame(() => {
        validationFramePending = false;
        flushFormValidation();
      });
    };

    // Initial state
    flushFormValidation();

    // Update button state while typing (powermail toggles error classes on input/change)
    form.addEventListener(
      'input',
      (e) => {
        if (e.target instanceof HTMLElement && form.contains(e.target)) scheduleFormValidation();
      },
      true,
    );

    // Change: checkbox/radio/select updates for button state
    form.addEventListener(
      'change',
      (e) => {
        if (e.target instanceof HTMLElement && form.contains(e.target)) scheduleFormValidation();
      },
      true,
    );

    // Powermail may manipulate DOM/classes asynchronously; observe and update after mutations.
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        const target = m.target instanceof HTMLElement ? m.target : null;
        if (!target || !form.contains(target)) continue;
        scheduleFormValidation();
        break;
      }
    });
    observer.observe(form, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'aria-invalid'],
    });
  });
}

